import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Compliance certifications — faithful rewrite of legacy
// molecules/ComplianceCertifications (Strapi components.compliance-page).
// Centered header over a two-up grid of horizontal cards: a large badge on the
// left, certification name + description on the right.
export const complianceCardsSchema = z.object({
  heading: z.string().optional(),
  description: z.string().optional(),
  cards: z
    .array(
      z.object({
        imageUrl: z.string().optional(),
        title: z.string().min(1),
        description: z.string().optional(),
      }),
    )
    .min(1),
});

type ComplianceCardsProps = z.infer<typeof complianceCardsSchema>;

const Section = styled.section`
  max-width: 112rem;
  margin: 0 auto;
  padding: 4rem 2rem;

  @media (min-width: 48em) {
    padding: 5rem 1.6rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Heading = styled.h2`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.8rem;
  text-align: center;
  max-width: 90%;

  @media (min-width: 48em) {
    font-size: 4rem;
  }
`;

const Description = styled.p`
  margin: 1.6rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  line-height: 2.8rem;
  text-align: center;
  max-width: 85rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.4rem;
  margin-top: 4rem;

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.6rem;
  align-items: center;
  padding: 1.6rem;
  border-radius: 2rem;
  box-shadow: 1px -1px 0px 0px #b1c1e7 inset;

  @media (min-width: 48em) {
    grid-template-columns: 15rem 1fr;
  }
`;

const Badge = styled.img`
  width: 10rem;
  height: 10rem;
  object-fit: contain;

  @media (min-width: 48em) {
    width: 15rem;
    height: 15rem;
  }
`;

const Name = styled.h3`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.8rem;

  @media (min-width: 48em) {
    font-size: 2rem;
    line-height: 2.8rem;
  }
`;

const CardDesc = styled.p`
  margin: 0.6rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
  line-height: 1.6;

  @media (min-width: 48em) {
    font-size: 1.6rem;
  }
`;

function ComplianceCards({ heading, description, cards }: ComplianceCardsProps) {
  return (
    <Section>
      {heading || description ? (
        <Header>
          {heading ? <Heading>{heading}</Heading> : null}
          {description ? <Description>{description}</Description> : null}
        </Header>
      ) : null}
      <Grid>
        {cards.map((card, i) => (
          <Card key={`${card.title}-${i}`}>
            {card.imageUrl ? <Badge src={card.imageUrl} alt="" /> : <span />}
            <div>
              <Name>{card.title}</Name>
              {card.description ? <CardDesc>{card.description}</CardDesc> : null}
            </div>
          </Card>
        ))}
      </Grid>
    </Section>
  );
}

export const complianceCardsBlock: BlockDefinition<ComplianceCardsProps> = {
  type: 'complianceCards',
  schema: complianceCardsSchema,
  Renderer: ComplianceCards,
};
